/**
 * GET /jaxrs/startup - the session bootstrap the app calls once at boot, and the source of the two
 * values every later request carries (`sessionHash`, `userConfiguration.currentContext`).
 *
 * The tool already calls it on mount for the session badge (client.js#fetchStartup), which shows six
 * fields out of it. This registers it as an operation so the whole payload is readable, and so the
 * context switch below is reachable.
 *
 * It answers 303 to the tenant-scoped /tenant/{companyId}/startup, so the side panel's "Redirected
 * to" is populated for this one - the request that returned the body is not the URL that was asked
 * for.
 *
 * A GET that can write, which is why `mutating` is a function here rather than a flag: filled in,
 * `switchContext` moves the session's current context and the whole app sees the new one, so it goes
 * behind the same arm-to-run guard as the writes. Left blank it is the plain session read it looks
 * like, and needs no arming.
 *
 * Web API only. The Task API's /me covers the user, not the session; see currentUser.js for why the
 * two are not registered as a pair.
 */
export const startup = {
    id: "startup",
    label: "Startup",
    summary: "Session bootstrap: the logged-in user, the current context and the session hash.",
    // only the context switch writes; a blank parameter leaves this a read - see endpoints.js#isMutating
    mutating: params => String(params["contextSelector:switchContext"] ?? "").trim() !== "",
    params: [
        {
            name: "contextSelector:switchContext",
            label: "Switch context to company",
            in: "query",
            appliesTo: ["web"],
            options: "companies",
            help: "Sent as contextSelector:switchContext, the spec's own name. Leave blank to read the session as it is. Picking a company switches the session's current context to it - a GET with a side effect, and the whole app sees the new context afterwards, not just this page.",
        },
    ],
    sides: {
        web: {
            method: "GET",
            path: "/startup",
            operationId: "get",
            scope: "session:read",
            query: params => ({"contextSelector:switchContext": params["contextSelector:switchContext"]}),
            extract: body => [body],
            shape: "StartupConfigurationDTO { sessionHash, loggedInUser, userConfiguration }",
        },
    },
};
